import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { ChevronsUpDown, Check, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizar, tokens as toTokens, coincideTodos } from "@/lib/searchNormalize";
import { useRecents } from "@/lib/useRecents";
import { useAuth } from "@/context/AuthContext";

const esTactil = () => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

/**
 * Selector buscable reutilizable (Radix Popover + cmdk) para entidades con muchos registros.
 * - Filtrado propio (sin acentos / mayúsculas / multi-palabra), NO el fuzzy de cmdk.
 * - `modal` para que el scroll (rueda y táctil) funcione dentro de Dialogs.
 * - Render por lotes (pageSize) + scroll incremental para listas grandes.
 * - Grupo "Recientes" por usuario (localStorage) si se pasa `recentsKey`.
 */
export function EntityCombobox({
  value,
  onChange,
  items = [],
  getLabel,
  getSecondary,
  getSearchText,
  getBadge,
  icon: Icon,
  placeholder = "Seleccionar…",
  searchPlaceholder = "Escribe para buscar…",
  emptyText = "Sin resultados",
  allowNone = false,
  noneLabel = "— Ninguno",
  disabled = false,
  testId = "entity-combobox",
  className = "",
  contentClassName = "",
  recentsKey = null,
  pageSize = 60,
  align = "start",
  secondaryInTrigger = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(pageSize);
  const listRef = useRef(null);
  const auth = useAuth();
  const [recentIds, pushRecent] = useRecents(recentsKey, auth?.user?.id);

  const indexed = useMemo(
    () => items.filter((it) => it && it.id).map((it) => ({
      it, id: it.id,
      lbl: normalizar(getLabel(it)),
      txt: normalizar(getSearchText ? getSearchText(it) : getLabel(it)),
    })),
    [items, getLabel, getSearchText]
  );

  const toks = useMemo(() => toTokens(query), [query]);

  const results = useMemo(() => {
    if (!toks.length) return indexed;
    const starts = [], rest = [];
    for (const r of indexed) {
      if (!coincideTodos(r.txt, toks)) continue;
      (r.lbl.startsWith(toks[0]) ? starts : rest).push(r);
    }
    return starts.concat(rest);
  }, [indexed, toks]);

  const selIdx = !toks.length && value ? results.findIndex((r) => r.id === value) : -1;
  const effLimit = selIdx >= limit ? selIdx + 20 : limit;
  const visible = results.slice(0, effLimit);
  const hayMas = results.length > visible.length;

  const recientes = useMemo(() => {
    if (toks.length || !recentIds.length) return [];
    return recentIds.map((id) => indexed.find((r) => r.id === id)).filter(Boolean);
  }, [recentIds, indexed, toks.length]);

  const sel = useMemo(() => items.find((it) => it && it.id === value) || null, [items, value]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setLimit(pageSize);
    const t = setTimeout(() => {
      listRef.current?.querySelector('[data-selected-item="true"]')?.scrollIntoView({ block: "center" });
    }, 60);
    return () => clearTimeout(t);
  }, [open, pageSize]);

  const elegir = (id) => {
    onChange(id);
    if (id) pushRecent(id);
    setOpen(false);
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (hayMas && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) setLimit((l) => l + pageSize);
  };

  const renderItem = (r, prefix = "") => {
    const isSel = r.id === value;
    const secondary = getSecondary?.(r.it);
    const badge = getBadge?.(r.it);
    return (
      <CommandItem
        key={prefix + r.id}
        value={prefix + r.id}
        onSelect={() => elegir(r.id)}
        data-selected-item={isSel ? "true" : undefined}
        data-testid={`${testId}-opt-${r.id}`}
        className="min-h-[46px] px-3 py-2 gap-2.5 cursor-pointer text-zinc-100 rounded-md data-[selected=true]:bg-zinc-800 data-[selected=true]:text-zinc-50 active:bg-zinc-700"
      >
        {Icon && <Icon className={cn("w-4 h-4 shrink-0", isSel ? "text-amber-400" : "text-zinc-500")} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate text-sm leading-tight">{getLabel(r.it)}</span>
            {badge}
          </div>
          {secondary && <div className="text-[11px] text-zinc-500 truncate leading-tight mt-0.5">{secondary}</div>}
        </div>
        {isSel && <Check className="w-4 h-4 text-amber-400 shrink-0" />}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full bg-zinc-900 border-zinc-800 justify-between font-normal text-zinc-100 hover:bg-zinc-900 hover:text-zinc-100 px-3",
            className
          )}
        >
          <span className="truncate text-left">
            {sel ? (
              <>
                <span>{getLabel(sel)}</span>
                {secondaryInTrigger && getSecondary?.(sel) && <span className="text-zinc-500 hidden sm:inline"> · {getSecondary(sel)}</span>}
              </>
            ) : (
              <span className="text-zinc-500">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        collisionPadding={8}
        onOpenAutoFocus={(e) => { if (esTactil()) e.preventDefault(); }}
        className={cn(
          "p-0 bg-zinc-950 border-zinc-800 w-[var(--radix-popover-trigger-width)] min-w-[min(340px,calc(100vw-1rem))] max-w-[calc(100vw-1rem)]",
          contentClassName
        )}
        data-testid={`${testId}-content`}
      >
        <Command className="bg-zinc-950" shouldFilter={false} loop>
          <CommandInput
            autoFocus={!esTactil()}
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className="text-zinc-100 h-11 text-base sm:text-sm"
            data-testid={`${testId}-input`}
          />
          <CommandList
            ref={listRef}
            onScroll={onScroll}
            style={{ touchAction: "pan-y", WebkitOverflowScrolling: "touch" }}
            className="dnf-scroll max-h-[min(50vh,380px)] overflow-y-auto overscroll-contain"
          >
            <CommandEmpty className="text-zinc-500 text-xs py-6 text-center" data-testid={`${testId}-empty`}>{emptyText}</CommandEmpty>
            {allowNone && !toks.length && (
              <CommandGroup>
                <CommandItem value="__none__" onSelect={() => elegir("")} data-testid={`${testId}-opt-none`}
                  className="min-h-[40px] px-3 text-zinc-500 italic cursor-pointer data-[selected=true]:bg-zinc-800">
                  {noneLabel}
                </CommandItem>
              </CommandGroup>
            )}
            {recientes.length > 0 && (
              <CommandGroup heading={
                <span className="flex items-center gap-1 text-zinc-500"><Clock className="w-3 h-3" /> Recientes</span>
              } data-testid={`${testId}-recientes`}>
                {recientes.map((r) => renderItem(r, "r__"))}
              </CommandGroup>
            )}
            <CommandGroup heading={recientes.length > 0 ? <span className="text-zinc-500">Todos</span> : undefined}>
              {visible.map((r) => renderItem(r))}
            </CommandGroup>
            {hayMas && (
              <div className="px-3 py-2 text-[11px] text-zinc-500 flex items-center justify-between border-t border-zinc-800/60">
                <span data-testid={`${testId}-counter`}>{visible.length} de {results.length.toLocaleString("es-ES")}</span>
                <button type="button" onClick={() => setLimit((l) => l + pageSize)} className="text-amber-300 hover:underline"
                  data-testid={`${testId}-more`}>Mostrar más</button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default EntityCombobox;
